
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter, namespace, State, Mutation } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { debounce, get } from "lodash";
import { CancelTokenSource } from "axios";

import { getDebouncePeriod } from "@/utils/debounce-helper";
import { getNewCancelTokenSource } from "@/utils/cancellableQueryHelper";
import {
	CancellableQuery,
	FeaturesList,
	PaginatedSearchQueryParams,
	UserGroup,
	UserPermissions,
	VueBootstrapField
} from "@/store/types";
import { DeviceTypeCounts, DeviceRecordsRetrievalStatus, DeviceTypeIdentifier, ServerDetails, ServerType } from "@/store/devices/types";
import { getNewServerDetailsTemplate, getRecordsRetrievalStatusDefaults } from "@/store/devices/templates";
import api from "@/services/api.service";
import ChildDeviceTabWrapper from "@/components/device-setup/tabs/ChildDeviceTabWrapper.vue";
import DeleteDeviceModal from "@/components/device-setup/confirmation-modals/DeleteDeviceModal.vue";
import DeviceEdit from "@/components/device-setup/editing/DeviceEdit.vue";
import SureViewIcon from "@/components/SureViewIcon.vue";
import GetConfig from "../GetConfig.vue";
import AlarmSetupTable from "@/components/setup/alarm-setup/AlarmSetupTable.vue";

export interface ServerModelDetails {
	invalid: boolean;
	selectedServer: ServerDetails;
}

const Devices = namespace("devices");

@Component({
	components: {
		"child-device-tab-wrapper": ChildDeviceTabWrapper,
		"delete-device-modal": DeleteDeviceModal,
		"device-edit": DeviceEdit,
		"sureview-icon": SureViewIcon,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		GetConfig: GetConfig,
		"alarm-setup-table": AlarmSetupTable
	}
})
export default class DeviceTable extends Vue {
	@Prop({ required: true, default: false, type: Boolean }) private visible;
	@Getter private getFeaturesList: FeaturesList;
	@Action private fetchUserGroups: () => Promise<void>;
	@Getter("getPermissions") private permissions: UserPermissions;
	@State private featuresList: FeaturesList;
	@State private UserGroups: UserGroup[];

	@Mutation private setIsLoading: (loading: boolean) => void;

	@Devices.Action private addServer: (server: ServerDetails) => Promise<void>;
	@Devices.Action private getDeviceTypes: () => Promise<void>;
	@Devices.Action private getFilteredServersPaginated: (
		params: CancellableQuery<PaginatedSearchQueryParams>
	) => Promise<void>;
	@Devices.Action private getServerLineProfiles: () => Promise<void>;
	@Devices.Action private updateServer: (server: ServerDetails) => Promise<void>;
	@Devices.Mutation private setCurrentServer: (server: ServerDetails) => void;

	@Devices.Mutation private updateCurrentServerRecordsRetrievalStatus: (
		recordsRetrievalStatus: DeviceRecordsRetrievalStatus
	) => void;
	@Devices.Mutation private updateRecordsRetrievalStatus: (
		recordsRetrievalStatus: DeviceRecordsRetrievalStatus
	) => void;
	@Devices.Mutation setServerModalVisible: (IsVisible: boolean) => void;

	@Devices.State private currentServer: ServerDetails;
	@Devices.State private recordsRetrievalStatus: DeviceRecordsRetrievalStatus;
	@Devices.State private serversList: ServerDetails[];
	@Devices.State private serverTypes: ServerType[];
	@Devices.State private serverModalOpened: boolean;

	@Prop() devices: ServerDetails[];

	private showModal: boolean = false;
	private alarmTabLoaded: boolean = false;


	private selectedServer = ({
		title: "",
		serverId: 0
	} as unknown) as ServerDetails;

	private async openModal(row) {
		this.showModal = true;
		this.selectedServer = row.item;
	}

	private filter = "";

	private axiosCancelTokenSource: CancelTokenSource = null;
	private baseChildDeviceTypeIds: number[] = [];
	private defaultResponseGrouping: string = "";
	private displayFilteredRecords: boolean = false;
	private filteredRecordsPage: number = 1;
	private loadingMoreRecords: boolean = false;
	private recordsPage: number = 1;
	private recordsPageSize: number = 100;
	private searchTerm: string = "";
	private searchTermPersisted: string = "";
	private serverModelDetails: ServerModelDetails = { invalid: true, selectedServer: null };
	private showDeleteModal: boolean = false;
	private showEditModal: boolean = false;
	private tabIndex: number = 0;
	private tableIsBusy: boolean = false;

	private fields: VueBootstrapField[] = [
		{ key: "title", label: "Title", sortable: true },
		{
			key: "serverTypeID",
			label: "Type",
			sortable: true,
			formatter: serverTypeID => {
				return this.getServerTypeTitle(serverTypeID);
			},
			sortByFormatted: true
		},
		{
			key: "groupID",
			label: "Area",
			sortable: true,
			formatter: groupID => {
				return this.getGroupTitle(groupID);
			},
			sortByFormatted: true
		},
		{ key: "host", label: "Host", sortable: true },
		{ key: "port", label: "Port", sortable: true }
	];

	private get isGetConfigInDevicesEnabled(): boolean {
		return get(this.getFeaturesList, ["Devices", "ShowGetConfig"]);
	}

	private get showGetConfig(): boolean {
		return (this.serverModelDetails != null && this.serverModelDetails.selectedServer != null && this.selectedServerType != null &&
		        this.selectedServerType.canGetConfig && this.isGetConfigInDevicesEnabled);
	}

	private get isGetConfigDisabled(): boolean {
		//return false;
		return (this.serverModelDetails.selectedServer == null || this.serverModelDetails.selectedServer.serverID == null || this.serverModelDetails.selectedServer.serverID == 0);
	}

	private get getConfigTooltipText(): string {
		return this.isGetConfigDisabled ? "Please click save to create the server before getting config" : "";
	}

	private async created(): Promise<void> {
		this.addChildDeviceTypes();

		if (this.hasAdvancedAlarmGroupingFlag) {
			this.addAdvancedAlarmGroupingSpecificFields();
		}

		if (this.hasEditPermission) {
			this.addEditPermissionsSpecificFields();
		}

		await Promise.all([
			this.getServers(),
			this.getDeviceTypes(),
			this.fetchUserGroups(),
			this.getDefaultResponseGrouping(),
			this.getServerLineProfiles()
		]);
	}

	private addChildDeviceTypes(): void {
		for (let deviceTypeIdentifier in DeviceTypeIdentifier) {
			const id = Number(deviceTypeIdentifier);

			if (!isNaN(id)) {
				this.baseChildDeviceTypeIds.push(id);
			}
		}
	}

	private addAdvancedAlarmGroupingSpecificFields(): void {
		this.fields.push({
			key: "alarmGrouping",
			label: "Alarm Grouping",
			sortable: true,
			formatter: (value, key, item) => this.getAlarmGroupingTitle(item)
		});
	}

	private get hasEditPermission(): boolean {
		return this.permissions.isAdmin || this.permissions.canEditSiteSetup;
	}

	private addEditPermissionsSpecificFields(): void {
		this.fields.push({ key: "actions", label: "Actions", sortable: false, class: "text-center" });
	}

	private async getServers(): Promise<void> {
		this.tableIsBusy = true;

		this.axiosCancelTokenSource = getNewCancelTokenSource(this.axiosCancelTokenSource, true);

		const cancellableQuery: CancellableQuery<PaginatedSearchQueryParams> = {
			params: { page: this.recordsPage, pageSize: this.recordsPageSize },
			cancelTokenSource: this.axiosCancelTokenSource
		};

		if (this.searchTerm) {
			cancellableQuery.params.page = this.filteredRecordsPage;
			cancellableQuery.params.searchTerm = this.searchTerm;
		}

		await this.getFilteredServersPaginated(cancellableQuery);

		this.tableIsBusy = false;
	}

	/** If the alarm doesn't have any grouping options set,
	 * then grouping is set in accordance with settings  */
	private async getDefaultResponseGrouping(): Promise<void> {
		const { data }: { data: string } = await api.getDefaultResponseGrouping();
		this.defaultResponseGrouping = data;
	}

	private getServerTypeTitle(serverTypeID: number): string {
		return this.serverTypes.find(st => st.serverTypeID == serverTypeID)?.title;
	}

	private get selectedServerType(): ServerType {
		return this.serverTypes.find(st => st.serverTypeID === this.serverModelDetails.selectedServer.serverTypeID);
	}

	private getGroupTitle(groupID: number): string {
		return this.UserGroups.find(ug => ug.groupID == groupID)?.title;
	}

	private get hasAdvancedAlarmGroupingFlag(): boolean {
		return get(this.featuresList, ["Alarms", "AdvancedAlarmGrouping"]);
	}

	private getAlarmGroupingTitle(server: ServerDetails): string {
		if (server.isRaiseIndividual) {
			return "No Grouping";
		}

		if (server.isRaiseGrouped) {
			return "By Response";
		}

		return this.defaultResponseGrouping;
	}

	private get serversToDisplay(): ServerDetails[] {
		if (this.displayFilteredRecords) {
			const searchTerm = this.searchTerm.toLowerCase();
			const searchTermAsNumber = Number(this.searchTerm);
			const searchNumericFields = !isNaN(searchTermAsNumber);

			return this.serversList.filter(
				s =>
					s.title.toLowerCase().includes(searchTerm) ||
					(s.host && s.host.toLowerCase().includes(searchTerm)) ||
					(searchNumericFields && s.port.toString().toLowerCase() === searchTerm) ||
					this.UserGroups.find(g => g.groupID === s.groupID)
						?.title.toLowerCase()
						.includes(searchTerm) ||
					this.serverTypes
						.find(st => st.serverTypeID === s.serverTypeID)
						?.title.toLowerCase()
						.includes(searchTerm)
			);
		}

		return this.serversList;
	}

	private get allRecordsInCurrentQueryAreRetrieved(): boolean {
		return this.displayFilteredRecords
			? this.recordsRetrievalStatus.allFilteredServersRetrieved
			: this.recordsRetrievalStatus.allServersRetrieved;
	}

	private async handleScrollBarReachEnd(): Promise<void> {
		if (!this.visible || this.tableIsBusy || this.allRecordsInCurrentQueryAreRetrieved || this.loadingMoreRecords) {
			return;
		}

		this.loadingMoreRecords = true;
		this.searchTerm ? this.filteredRecordsPage++ : this.recordsPage++;

		await this.getServers();

		this.loadingMoreRecords = false;
	}

	private handleAddDevice(): void {
		this.setIsLoading(true);
		this.setCurrentServer(getNewServerDetailsTemplate());
		this.setServerModalVisible(true);
		this.showEditModal = true;
	}

	private handleEditModalCloseClick(): void {
		this.setServerModalVisible(false);
		this.showEditModal = false;
		this.setCurrentServer(null);
	}

	private handleEditClick(server: ServerDetails): void {
		this.setIsLoading(true);
		this.setCurrentServer(server);
		this.updateCurrentServerRecordsRetrievalStatus(getRecordsRetrievalStatusDefaults());
		this.setServerModalVisible(true);
		this.showEditModal = true;
	}

	private handleDeleteClick(server: ServerDetails): void {
		this.setCurrentServer(server);
		this.showDeleteModal = true;
	}

	private handleDeleteModalClose(): void {
		this.showDeleteModal = false;
		this.setCurrentServer(null);
	}

	private get editModalTitle(): string {
		if (this.isEditingServer) {
			return `Editing ${this.currentServer.title}`;
		}

		return "Create new device";
	}

	private get isEditingServer(): boolean {
		return this.currentServer && this.currentServer.serverID > 0;
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
		await this.performSearch(searchTerm);
	}, getDebouncePeriod());

	private async performSearch(searchTerm: string): Promise<void> {
		const newRetrievalStatus: DeviceRecordsRetrievalStatus = {
			...this.recordsRetrievalStatus,
			allFilteredServersRetrieved: false
		};

		this.updateRecordsRetrievalStatus(newRetrievalStatus);

		this.searchTerm = searchTerm;

		if (!this.searchTerm) {
			this.displayFilteredRecords = false;
			return;
		}

		await this.getServers();
		this.displayFilteredRecords = true;
	}

	private get childDeviceTypeIds(): number[] {
		if (this.isEditingServer) {
			return this.baseChildDeviceTypeIds;
		}

		return [];
	}

	private handleServerModelDetailsChanged(serverModelDetails: ServerModelDetails): void {
		this.serverModelDetails = serverModelDetails;
	}

	private async handleServerSaveClick(): Promise<void> {
		if (this.serverModelDetails.selectedServer.serverID) {
			let selectedServer = this.serverModelDetails.selectedServer;
			selectedServer.extraValue = this.extraValue;
			await this.updateServer(selectedServer);
			return;
		}

		await this.addServer(this.serverModelDetails.selectedServer);
	}

	private deviceEditComponentLoaded(): void {
		this.setIsLoading(false);
	}

	public get extraValue(): string {

		if (this.currentServer) {
			return this.currentServer.extraValue;
		}

		return "";
	}

	private get allowsAlarms(): boolean {
		const serverType: ServerType = this.serverTypes.find(
				st => st.serverTypeID === this.currentServer.serverTypeID
			);
		return serverType && serverType.numAlarmInputs !== 0;
	}

	private get alarmCount(): number {
		let aCount = 0;
		if (this.currentServer != null && this.currentServer.deviceTypeCounts != null && this.currentServer.deviceTypeCounts.alarms != null) {
			aCount = this.currentServer.deviceTypeCounts.alarms;
		}

		return aCount;
	}

	private get selectedServerId(): number {
		let id = 0;
		if (this.currentServer != null) {
			id =  this.currentServer.serverID;
		}

		return id;
	}

	private handleTabLoaded(): void {
		this.alarmTabLoaded = true;
	}

	private get getAlarmTitleText(): string {
		let text = "";
		if (!this.allowsAlarms) {
			text = `Alarms are not supported on this device`;
		}
		return text;
	}
}
